import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート

// APIサーバーのURL
const API_URL = "https://salesbreaker.jp";

// --- Components ---

/**
 * ページネーションコンポーネント
 * @param {object} props - コンポーネントのプロパティ
 * @param {number} props.currentPage - 現在のページ番号
 * @param {number} props.totalPages - 全ページ数
 * @param {function} props.onPageChange - ページ変更時のコールバック関数
 * @param {number} props.totalItems - 全アイテム数
 * @param {number} props.itemsPerPage - 1ページあたりのアイテム数
 * @param {number} props.currentItemCount - 現在のページに表示されているアイテム数
 */
function Pagination({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, currentItemCount }) {
    // ページが1つ以下の場合は何も表示しない
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = startItem + currentItemCount - 1;

    return (
        <div className="flex flex-col gap-3 mt-4 p-4 border-t sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-700">
                {totalItems > 0 ? `${totalItems.toLocaleString()} 件中 ${startItem.toLocaleString()} - ${endItem.toLocaleString()} 件を表示` : '該当するユーザーはいません'}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <button 
                    onClick={() => onPageChange(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                    前へ
                </button>
                <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
                <button 
                    onClick={() => onPageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                    className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                    次へ
                </button>
            </div>
        </div>
    );
}

/**
 * 未契約ユーザーフォローページコンポーネント
 */
function PendingUsersPage() {
    const { user } = useOutletContext();
    const [pendingUsers, setPendingUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // 検索入力のデバウンス処理
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // 検索条件が変わったら1ページ目に戻す
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // 未契約ユーザーのデータをAPIから取得する関数
    const fetchPendingUsers = useCallback(async () => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: currentPage,
            limit: 50,
            searchTerm: debouncedSearchTerm,
        });

        try {
            const res = await fetch(`${API_URL}/api/admin/pending-users?${params.toString()}`, {
                headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
            });
            const data = await res.json();
            if (data.success) {
                setPendingUsers(data.users || []);
                setPagination(data.pagination);
            } else {
                alert('未契約ユーザーの取得に失敗しました: ' + data.error);
            }
        } catch (error) {
            console.error(error);
            alert('通信エラーが発生しました。');
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearchTerm]);

    // ページ番号や検索キーワードが変更されたらデータを再取得
    useEffect(() => {
        fetchPendingUsers();
    }, [fetchPendingUsers]);

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">⏳ 未契約ユーザーフォロー</h1>
            
            {/* 検索フィルター */}
            <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
                <input
                    type="text"
                    placeholder="会社名、氏名、連絡先で検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border rounded-md"
                />
            </div>

            {/* ユーザー一覧テーブル */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                {isLoading ? (
                    <p className="p-8 text-center">読み込み中...</p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <div className="divide-y divide-gray-100 md:hidden">
                                {pendingUsers.length > 0 ? pendingUsers.map(pUser => (
                                    <div key={`${pUser.id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-900 break-words">{pUser.company || '会社名なし'}</p>
                                                <p className="mt-1 text-xs text-gray-500">{pUser.created_at ? new Date(pUser.created_at).toLocaleString('ja-JP') : '---'}</p>
                                            </div>
                                            {pUser.is_verified === 1 ? (
                                                <span className="flex-shrink-0 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">認証済み</span>
                                            ) : (
                                                <span className="flex-shrink-0 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">未認証</span>
                                            )}
                                        </div>
                                        <div className="mt-3 space-y-1 text-sm text-gray-600">
                                            <p>{pUser.name || '担当者名なし'}</p>
                                            <p className="break-all">{pUser.email}</p>
                                            <p>{pUser.phone || '電話番号なし'}</p>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="px-4 py-10 text-center text-sm text-gray-500">該当するユーザーはいません</div>
                                )}
                            </div>
                            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                    <tr>
                                        {/* ★修正点1: テーブルヘッダーに「認証状態」を追加 */}
                                        <th className="py-3 px-4">登録日時</th>
                                        <th className="py-3 px-4">会社名</th>
                                        <th className="py-3 px-4">担当者名</th>
                                        <th className="py-3 px-4">メールアドレス</th>
                                        <th className="py-3 px-4">電話番号</th>
                                        <th className="py-3 px-4">認証状態</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {pendingUsers.map(pUser => (
                                        <tr key={pUser.id}>
                                            {/* ★修正点2: created_atが存在しない場合のエラーを回避 */}
                                            <td className="py-3 px-4">
                                                {pUser.created_at ? new Date(pUser.created_at).toLocaleString('ja-JP') : '---'}
                                            </td>
                                            <td className="py-3 px-4 font-semibold">{pUser.company}</td>
                                            <td className="py-3 px-4">{pUser.name}</td>
                                            <td className="py-3 px-4">{pUser.email}</td>
                                            <td className="py-3 px-4">{pUser.phone}</td>
                                            {/* ★修正点3: is_verifiedの値に応じて認証状態を表示 */}
                                            <td className="py-3 px-4">
                                                {pUser.is_verified === 1 ? (
                                                    <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                                                        ✅ 認証済み
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                                                        ❌ 未認証
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination 
                            currentPage={pagination ? pagination.currentPage : 1}
                            totalPages={pagination ? pagination.totalPages : 1}
                            onPageChange={setCurrentPage}
                            totalItems={pagination ? pagination.totalItems : 0}
                            itemsPerPage={pagination ? pagination.itemsPerPage : 50}
                            currentItemCount={pendingUsers.length}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default PendingUsersPage;
